import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, ErrorSummary, Input, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  insurerApiConsoleService as svc,
  type ChangeRequest,
  type ConsoleEnv,
  type EnvOverview,
  type LogRow,
  type WebhookEndpoint,
} from '@/services/insurerApiConsole';
import { useAuthStore } from '@/store';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import { formatDateTime } from '@/utils';

/**
 * Insurer > API Console (#403). Backed by `/insurers/insurance/api-console/`, scoped on
 * the server to the signed-in insurer. Nothing on this page can switch production on or
 * change the API address: those are requests a Country Admin decides. No key or secret is
 * ever returned; a webhook signing secret is shown once, when it is created or rotated.
 */

type Tab = 'overview' | 'logs' | 'connections' | 'requests' | 'webhooks';
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'logs', label: 'Call log' },
  { id: 'connections', label: 'Connections' },
  { id: 'requests', label: 'Go-live and address' },
  { id: 'webhooks', label: 'Webhooks' },
];
const ENVS: ConsoleEnv[] = ['production', 'sandbox'];
const muted = 'text-sm text-gray-500 dark:text-gray-400';

function chipVariant(state: string): 'success' | 'error' | 'warning' | 'default' {
  if (state === 'active') return 'success';
  if (state === 'test_failed') return 'error';
  if (state === 'configured_untested') return 'warning';
  return 'default';
}

function State({ loading, error, empty, emptyText }: { loading: boolean; error: unknown; empty?: boolean; emptyText?: string }) {
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorSummary messages={[getErrorMessage(error)]} />;
  if (empty) return <p className={muted}>{emptyText ?? 'Nothing here yet.'}</p>;
  return null;
}

function pct(v: number | null) {
  return v === null ? 'No calls yet' : `${v}%`;
}

// ---------------------------------------------------------------------------

function EnvCard({ o }: { o: EnvOverview }) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold capitalize text-gray-900 dark:text-white">{o.environment}</h3>
        <Badge variant={chipVariant(o.status.state)}>{o.status.label}</Badge>
      </div>
      {!o.has_calls ? (
        <p className={muted}>No calls yet in this environment.</p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700 dark:text-gray-300">
          <dt>Calls, last 24 hours</dt>
          <dd>{o.last_24h.calls} ({pct(o.last_24h.success_rate_pct)} succeeded)</dd>
          <dt>Calls, last 7 days</dt>
          <dd>{o.last_7d.calls} ({pct(o.last_7d.success_rate_pct)} succeeded)</dd>
          <dt>Response time</dt>
          <dd>median {o.latency_ms.p50 ?? '-'} ms, slowest 5% {o.latency_ms.p95 ?? '-'} ms</dd>
          <dt>Last success</dt>
          <dd>{o.last_success_at ? formatDateTime(o.last_success_at) : 'Never'}</dd>
          <dt>Last failure</dt>
          <dd>{o.last_failure_at ? formatDateTime(o.last_failure_at) : 'Never'}</dd>
        </dl>
      )}
      <p className={`${muted} mt-3`}>
        {o.configured
          ? `API version ${o.active_api_version || '-'}. Last connection test: ${o.last_test.at ? formatDateTime(o.last_test.at) : 'not yet'}${o.last_test.ok === false ? ' (failed)' : ''}.`
          : 'Not set up yet. A Country Admin sets the API address and tests it.'}
      </p>
      {o.top_error_codes.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Most common errors (7 days)</p>
          <ul className="text-sm text-gray-600 dark:text-gray-300">
            {o.top_error_codes.map((e) => (
              <li key={e.code}>
                <code>{e.code}</code> x {e.count}
              </li>
            ))}
          </ul>
        </div>
      )}
      {o.endpoints.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="pr-3 py-1">Call</th>
                <th className="pr-3 py-1">Calls</th>
                <th className="pr-3 py-1">Success</th>
                <th className="py-1">Avg ms</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              {o.endpoints.map((e) => (
                <tr key={e.endpoint_name} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="pr-3 py-1">{e.endpoint_name}</td>
                  <td className="pr-3 py-1">{e.calls}</td>
                  <td className="pr-3 py-1">{pct(e.success_rate_pct)}</td>
                  <td className="py-1">{e.avg_latency_ms}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function OverviewTab() {
  const q = useQuery({ queryKey: ['api-console', 'overview'], queryFn: () => svc.overview() });
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {q.isLoading || q.isError ? <State loading={q.isLoading} error={q.error} /> : ENVS.filter((e) => q.data?.[e]).map((e) => <EnvCard key={e} o={q.data![e]} />)}
    </div>
  );
}

// ---------------------------------------------------------------------------

function JsonBlock({ value, truncated }: { value: unknown; truncated: boolean }) {
  // React escapes text children, so an insurer's own HTML or script in a body is shown, not run.
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <>
      <pre className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 p-2 rounded overflow-auto max-h-64 whitespace-pre-wrap break-all">{text}</pre>
      {truncated && <p className={muted}>Shown up to 64 KB; the rest is cut.</p>}
    </>
  );
}

function LogDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['api-console', 'log', id], queryFn: () => svc.logDetail(id) });
  const d = q.data;
  return (
    <Modal isOpen onClose={onClose} title="Call detail" size="xl">
      {!d ? (
        <State loading={q.isLoading} error={q.error} />
      ) : (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>
            <b>{d.http_method}</b> {d.request_url} - {d.status}, HTTP {d.response_status_code ?? '-'}, {d.latency_ms} ms
            {d.platform_error_code && <> - <code>{d.platform_error_code}</code></>}
          </p>
          <p className={muted}>Personal details (names, phone, e-mail, address, date of birth, PAN, Aadhaar, bank, health) are hidden so you can check structure and codes.</p>
          {d.error_message && <p>{d.error_message}</p>}
          <div><b>Request headers</b><JsonBlock value={d.request_headers} truncated={false} /></div>
          <div><b>Request body</b><JsonBlock value={d.request_body} truncated={d.request_truncated} /></div>
          <div><b>Response headers</b><JsonBlock value={d.response_headers} truncated={false} /></div>
          <div><b>Response body</b><JsonBlock value={d.response_body} truncated={d.response_truncated} /></div>
          {d.retry_chain.length > 0 && (
            <div>
              <b>Retries</b>
              <ul>
                {d.retry_chain.map((r) => (
                  <li key={r.id}>#{r.retry_attempt}: {r.status} {r.response_status_code ?? ''} at {formatDateTime(r.started_at)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <ModalFooter><Button variant="outline" onClick={onClose}>Close</Button></ModalFooter>
    </Modal>
  );
}

function LogsTab({ isAdmin }: { isAdmin: boolean }) {
  const [filters, setFilters] = useState<Record<string, string>>({ environment: '', status: '', q: '', date_from: '', date_to: '' });
  const [open, setOpen] = useState<string | null>(null);
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const q = useQuery({ queryKey: ['api-console', 'logs', params], queryFn: () => svc.logs(params) });
  const exportCsv = useMutation({
    mutationFn: () => svc.exportLogs(params),
    onSuccess: (blob) => {
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'api-call-log.csv';
      a.click();
      URL.revokeObjectURL(href);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const rows: LogRow[] = q.data?.results ?? [];
  const selectCls = 'block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm p-2';
  return (
    <Card>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mb-4">
        <select aria-label="Environment" className={selectCls} value={filters.environment} onChange={set('environment')}>
          <option value="">Both environments</option>
          <option value="production">Production</option>
          <option value="sandbox">Sandbox</option>
        </select>
        <select aria-label="Result" className={selectCls} value={filters.status} onChange={set('status')}>
          <option value="">Any result</option>
          <option value="error,timeout,circuit_broken">Failures</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
          <option value="timeout">Timeout</option>
        </select>
        <Input aria-label="From date" type="date" value={filters.date_from} onChange={set('date_from')} />
        <Input aria-label="To date" type="date" value={filters.date_to} onChange={set('date_to')} />
        <Input aria-label="Search error text" placeholder="Search error text" value={filters.q} onChange={set('q')} />
      </div>
      <p className={`${muted} mb-2`}>Shows the last 7 days unless you pick dates (at most 31 days). Bodies are kept 90 days.</p>
      {isAdmin && (
        <Button variant="outline" size="sm" isLoading={exportCsv.isPending} onClick={() => exportCsv.mutate()}>
          Export CSV (no bodies)
        </Button>
      )}
      <div className="mt-3 overflow-x-auto">
        <State loading={q.isLoading} error={q.error} empty={!q.isLoading && !q.error && rows.length === 0} emptyText="No calls match." />
        {rows.length > 0 && (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="pr-3 py-1">When</th><th className="pr-3 py-1">Env</th><th className="pr-3 py-1">Call</th>
                <th className="pr-3 py-1">Result</th><th className="pr-3 py-1">HTTP</th><th className="pr-3 py-1">ms</th><th className="pr-3 py-1">Broker</th><th />
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="pr-3 py-1">{formatDateTime(r.started_at)}</td>
                  <td className="pr-3 py-1">{r.environment}</td>
                  <td className="pr-3 py-1">{r.endpoint_name}</td>
                  <td className="pr-3 py-1"><Badge variant={r.status === 'success' ? 'success' : 'error'} size="sm">{r.status}</Badge></td>
                  <td className="pr-3 py-1">{r.response_status_code ?? '-'}</td>
                  <td className="pr-3 py-1">{r.latency_ms}</td>
                  <td className="pr-3 py-1">{r.broker_code}</td>
                  <td className="py-1"><Button size="sm" variant="outline" onClick={() => setOpen(r.id)}>Details</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {open && <LogDetailModal id={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function ConnectionsTab() {
  const q = useQuery({ queryKey: ['api-console', 'connections'], queryFn: () => svc.connections() });
  const rows = q.data ?? [];
  return (
    <Card>
      <p className={`${muted} mb-3`}>
        Each broker keeps its own key for your API. You see whether a key is saved and the last characters of it, never the key. To
        ask for a new key, contact the broker.
      </p>
      <State loading={q.isLoading} error={q.error} empty={!q.isLoading && !q.error && rows.length === 0} emptyText="No broker is connected to your company yet." />
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="pr-3 py-1">Broker</th><th className="pr-3 py-1">Status</th>
                {ENVS.map((e) => <th key={e} className="pr-3 py-1 capitalize">{e} key</th>)}
                <th className="py-1">Last call</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              {rows.map((r) => (
                <tr key={r.broker_code} className="border-t border-gray-100 dark:border-gray-700 align-top">
                  <td className="pr-3 py-1">{r.broker_code}</td>
                  <td className="pr-3 py-1">{r.relation_status}</td>
                  {ENVS.map((e) => (
                    <td key={e} className="pr-3 py-1">
                      {r.environments[e].credential_saved ? <>Saved <code>{r.environments[e].key_hint}</code></> : 'Not saved'}
                    </td>
                  ))}
                  <td className="py-1">
                    {r.environments.production.last_success_at ? formatDateTime(r.environments.production.last_success_at) : 'No successful call yet'}
                    {r.environments.production.last_error_at && <div className={muted}>Last error {formatDateTime(r.environments.production.last_error_at)}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

const CHECKLIST: { key: string; label: string }[] = [
  { key: 'production_endpoint_given', label: 'We have given the production API address' },
  { key: 'egress_whitelisted', label: 'Our firewall allows the Policy.Online server addresses' },
  { key: 'written_signoff', label: 'We have written sign-off from our business owner' },
  { key: 'master_data_covered', label: 'Our product and master data are loaded' },
];

function RequestsTab({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['api-console', 'requests'], queryFn: () => svc.requests() });
  const [note, setNote] = useState('');
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [baseUrl, setBaseUrl] = useState('');
  const [env, setEnv] = useState<ConsoleEnv>('production');
  const done = () => qc.invalidateQueries({ queryKey: ['api-console', 'requests'] });
  const send = useMutation({
    mutationFn: (body: Record<string, unknown>) => svc.createRequest(body),
    onSuccess: () => {
      toast.success('Request sent to the Country Admin');
      setNote('');
      setBaseUrl('');
      done();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cancel = useMutation({ mutationFn: (id: string) => svc.cancelRequest(id), onSuccess: done, onError: (e) => toast.error(getErrorMessage(e)) });
  const label = (r: ChangeRequest) => (r.kind === 'go_live' ? 'Go live in production' : `New ${r.environment} API address: ${String(r.payload.base_url ?? '')}`);
  const variant = (s: ChangeRequest['status']) => (s === 'approved' ? 'success' : s === 'declined' ? 'error' : s === 'pending' ? 'warning' : 'default');
  return (
    <div className="space-y-4">
      <Card>
        <p className={muted}>
          You cannot switch production on or change the API address yourself. Send a request here; a Country Admin checks it, sets the
          address, tests it and turns it on. Nothing you type here is contacted before they approve it.
        </p>
      </Card>
      {isAdmin && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Request go-live</h3>
            {CHECKLIST.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-0.5">
                <input type="checkbox" checked={!!checks[c.key]} onChange={(e) => setChecks((s) => ({ ...s, [c.key]: e.target.checked }))} />
                {c.label}
              </label>
            ))}
            <Input label="Note for the Country Admin" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="mt-2">
              <Button isLoading={send.isPending} onClick={() => send.mutate({ kind: 'go_live', environment: 'production', note, checklist: checks })}>
                Request go-live
              </Button>
            </div>
          </Card>
          <Card>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Request an API address change</h3>
            <select aria-label="Environment" className="block w-full mb-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm p-2" value={env} onChange={(e) => setEnv(e.target.value as ConsoleEnv)}>
              <option value="production">Production</option>
              <option value="sandbox">Sandbox</option>
            </select>
            <Input label="New address (https only)" placeholder="https://api.your-company.com" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
            <div className="mt-2">
              <Button isLoading={send.isPending} onClick={() => send.mutate({ kind: 'base_url', environment: env, base_url: baseUrl, note })}>
                Request change
              </Button>
            </div>
          </Card>
        </div>
      )}
      <Card>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Your requests</h3>
        <State loading={q.isLoading} error={q.error} empty={!q.isLoading && !q.error && (q.data ?? []).length === 0} emptyText="No requests yet." />
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {(q.data ?? []).map((r) => (
            <li key={r.id} className="py-2 flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-300">
              <div>
                <div>{label(r)}</div>
                <div className={muted}>{formatDateTime(r.requested_at)}{r.decision_note ? ` - ${r.decision_note}` : ''}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={variant(r.status)} size="sm">{r.status}</Badge>
                {isAdmin && r.status === 'pending' && <Button size="sm" variant="outline" onClick={() => cancel.mutate(r.id)}>Cancel</Button>}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SecretModal({ secret, onClose }: { secret: string; onClose: () => void }) {
  return (
    <Modal isOpen onClose={onClose} title="Copy your signing secret now" size="md">
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
        This is the only time it is shown. Use it to check the <code>X-PolicyOnline-Signature</code> header on every delivery.
      </p>
      <input readOnly aria-label="Signing secret" value={secret} onFocus={(e) => e.target.select()} className="w-full font-mono text-xs rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2" />
      <ModalFooter>
        <Button variant="outline" onClick={() => navigator.clipboard?.writeText(secret).then(() => toast.success('Copied'))}>Copy</Button>
        <Button onClick={onClose}>I have saved it</Button>
      </ModalFooter>
    </Modal>
  );
}

function EndpointForm({ initial, events, onClose, onSecret }: { initial: WebhookEndpoint | null; events: string[]; onClose: () => void; onSecret: (s: string) => void }) {
  const qc = useQueryClient();
  const [env, setEnv] = useState<ConsoleEnv>(initial?.environment ?? 'production');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [types, setTypes] = useState<string[]>(initial?.event_types ?? []);
  const [header, setHeader] = useState({ name: '', value: '' });
  const [formError, setFormError] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const headers = header.name ? { [header.name]: header.value } : undefined;
      return initial
        ? svc.updateWebhook(initial.id, initial.row_version, { url, description, event_types: types, ...(headers ? { headers } : {}) })
        : svc.createWebhook({ environment: env, url, description, event_types: types, ...(headers ? { headers } : {}) });
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['api-console', 'webhooks'] });
      if (r.secret) onSecret(r.secret);
      onClose();
    },
    onError: (e) => setFormError(getErrorMessage(e)),
  });
  return (
    <Modal isOpen onClose={onClose} title={initial ? 'Edit endpoint' : 'Add endpoint'} size="md">
      <div className="space-y-3">
        {formError && <ErrorSummary messages={[formError]} />}
        {!initial && (
          <select aria-label="Environment" className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm p-2" value={env} onChange={(e) => setEnv(e.target.value as ConsoleEnv)}>
            <option value="production">Production</option>
            <option value="sandbox">Sandbox</option>
          </select>
        )}
        <Input label="URL (https only)" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-company.com/hooks/policyonline" />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-200">Events</legend>
          {events.map((ev) => (
            <label key={ev} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={types.includes(ev)} onChange={(e) => setTypes((t) => (e.target.checked ? [...t, ev] : t.filter((x) => x !== ev)))} />
              <code>{ev}</code>
            </label>
          ))}
        </fieldset>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Extra header name (optional)" value={header.name} onChange={(e) => setHeader((h) => ({ ...h, name: e.target.value }))} />
          <Input label="Header value" type="password" value={header.value} onChange={(e) => setHeader((h) => ({ ...h, value: e.target.value }))} />
        </div>
        {initial && initial.header_names.length > 0 && <p className={muted}>Current headers: {initial.header_names.join(', ')} (values are hidden). Entering a header replaces them all.</p>}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button isLoading={save.isPending} onClick={() => { setFormError(''); save.mutate(); }}>{initial ? 'Save' : 'Add endpoint'}</Button>
      </ModalFooter>
    </Modal>
  );
}

function DeliveriesModal({ endpoint, onClose }: { endpoint: WebhookEndpoint; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['api-console', 'deliveries', endpoint.id], queryFn: () => svc.deliveries(endpoint.id) });
  const resend = useMutation({
    mutationFn: (id: string) => svc.resend(id),
    onSuccess: () => { toast.success('Sent again'); qc.invalidateQueries({ queryKey: ['api-console', 'deliveries', endpoint.id] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rows = q.data?.results ?? [];
  return (
    <Modal isOpen onClose={onClose} title="Delivery log" size="xl">
      <State loading={q.isLoading} error={q.error} empty={!q.isLoading && !q.error && rows.length === 0} emptyText="Nothing sent yet. Send a test event." />
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-gray-500 dark:text-gray-400"><th className="pr-3 py-1">When</th><th className="pr-3 py-1">Event</th><th className="pr-3 py-1">Result</th><th className="pr-3 py-1">Tries</th><th className="pr-3 py-1">Answer</th><th /></tr></thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700 align-top">
                  <td className="pr-3 py-1">{formatDateTime(r.created_at)}</td>
                  <td className="pr-3 py-1"><code>{r.event_type}</code></td>
                  <td className="pr-3 py-1">
                    <Badge variant={r.status === 'delivered' ? 'success' : r.status === 'failed' ? 'error' : 'warning'} size="sm">{r.status}</Badge>
                    {r.status === 'pending' && <div className={muted}>next {formatDateTime(r.next_attempt_at)}</div>}
                  </td>
                  <td className="pr-3 py-1">{r.attempt_count}</td>
                  <td className="pr-3 py-1 max-w-xs break-words">{r.last_status_code ? `HTTP ${r.last_status_code} ` : ''}{r.last_error}{r.last_response_snippet && <pre className="text-xs whitespace-pre-wrap">{r.last_response_snippet}</pre>}</td>
                  <td className="py-1">{!r.is_test && r.status !== 'pending' && <Button size="sm" variant="outline" onClick={() => resend.mutate(r.id)}>Resend</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ModalFooter><Button variant="outline" onClick={onClose}>Close</Button></ModalFooter>
    </Modal>
  );
}

function WebhooksTab({ isAdmin, canEdit }: { isAdmin: boolean; canEdit: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['api-console', 'webhooks'], queryFn: () => svc.webhooks() });
  const cat = useQuery({ queryKey: ['api-console', 'webhook-events'], queryFn: () => svc.webhookEvents() });
  const [form, setForm] = useState<WebhookEndpoint | null | 'new'>(null);
  const [log, setLog] = useState<WebhookEndpoint | null>(null);
  const [secret, setSecret] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['api-console', 'webhooks'] });
  const test = useMutation({
    mutationFn: (id: string) => svc.testWebhook(id),
    onSuccess: (r) => (r.status === 'delivered' ? toast.success(`Test delivered (HTTP ${r.last_status_code})`) : toast.error(`Test failed: ${r.last_error || 'no answer'}`)),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rotate = useMutation({ mutationFn: (id: string) => svc.rotateSecret(id), onSuccess: (r) => { setSecret(r.secret ?? ''); refresh(); }, onError: (e) => toast.error(getErrorMessage(e)) });
  const remove = useMutation({ mutationFn: (id: string) => svc.deleteWebhook(id), onSuccess: refresh, onError: (e) => toast.error(getErrorMessage(e)) });
  const toggle = useMutation({
    mutationFn: (w: WebhookEndpoint) => svc.updateWebhook(w.id, w.row_version, { is_active: !w.is_active }),
    onSuccess: refresh,
    onError: (e) => { toast.error(getErrorMessage(e)); refresh(); },
  });
  const rows = list.data ?? [];
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900 dark:text-white">Endpoints</h3>
          {canEdit && <Button size="sm" onClick={() => setForm('new')}>Add endpoint</Button>}
        </div>
        <State loading={list.isLoading} error={list.error} empty={!list.isLoading && !list.error && rows.length === 0} emptyText="No endpoints yet. Add one to receive events at your own address." />
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((w) => (
            <li key={w.id} className="py-3 text-sm text-gray-700 dark:text-gray-300">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={w.is_active ? 'success' : 'default'} size="sm">{w.is_active ? 'On' : 'Off'}</Badge>
                <Badge size="sm">{w.environment}</Badge>
                <span className="font-mono break-all">{w.url}</span>
              </div>
              <div className={muted}>
                {w.event_types.join(', ')}{w.description ? ` - ${w.description}` : ''}
                {w.last_triggered_at ? ` - last delivered ${formatDateTime(w.last_triggered_at)}` : ''}
              </div>
              {w.disabled_reason && <div className="text-red-600 dark:text-red-400">{w.disabled_reason}</div>}
              {w.previous_secret_valid_until && <div className={muted}>The old secret also works until {formatDateTime(w.previous_secret_valid_until)}.</div>}
              <div className="mt-2 flex flex-wrap gap-2">
                {canEdit && <Button size="sm" variant="outline" onClick={() => test.mutate(w.id)} disabled={!w.is_active}>Send test</Button>}
                <Button size="sm" variant="outline" onClick={() => setLog(w)}>Delivery log</Button>
                {canEdit && <Button size="sm" variant="outline" onClick={() => setForm(w)}>Edit</Button>}
                {canEdit && <Button size="sm" variant="outline" onClick={() => toggle.mutate(w)}>{w.is_active ? 'Switch off' : 'Switch on'}</Button>}
                {isAdmin && <Button size="sm" variant="outline" onClick={() => rotate.mutate(w.id)}>Rotate secret</Button>}
                {canEdit && <Button size="sm" variant="danger" onClick={() => window.confirm('Remove this endpoint?') && remove.mutate(w.id)}>Remove</Button>}
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Events and how to verify them</h3>
        <State loading={cat.isLoading} error={cat.error} />
        {cat.data && (
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p>Each request is a JSON object with <code>id</code>, <code>type</code>, <code>created_at</code>, <code>api_version</code> and <code>data</code>. {cat.data.signature.note}</p>
            <p><code>{cat.data.signature.header}: {cat.data.signature.format}</code>. Reject timestamps older than {cat.data.signature.tolerance_seconds / 60} minutes. Redirects are not followed.</p>
            {cat.data.events.map((e) => (
              <details key={e.type}>
                <summary><code>{e.type}</code> - {e.description}</summary>
                <pre className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 p-2 rounded overflow-auto">{JSON.stringify(e.sample, null, 2)}</pre>
              </details>
            ))}
          </div>
        )}
      </Card>
      {form && <EndpointForm initial={form === 'new' ? null : form} events={(cat.data?.events ?? []).map((e) => e.type)} onClose={() => setForm(null)} onSecret={setSecret} />}
      {log && <DeliveriesModal endpoint={log} onClose={() => setLog(null)} />}
      {secret && <SecretModal secret={secret} onClose={() => setSecret('')} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function ApiConsolePage() {
  const [tab, setTab] = useState<Tab>('overview');
  const { user } = useAuthStore();
  const { can } = useInsurerPermissions();
  const isAdmin = user?.role === 'insurer_admin';
  return (
    <div>
      <PageHeader title="API Console" subtitle="Check your connection, read the call log, receive events at your own address" breadcrumbs={[{ label: 'API Console' }]} />
      <div role="tablist" className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-md text-sm border ${tab === t.id ? 'bg-primary-600 text-white border-primary-600' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'overview' && <OverviewTab />}
      {tab === 'logs' && <LogsTab isAdmin={isAdmin} />}
      {tab === 'connections' && <ConnectionsTab />}
      {tab === 'requests' && <RequestsTab isAdmin={isAdmin} />}
      {tab === 'webhooks' && <WebhooksTab isAdmin={isAdmin} canEdit={can('api_console.edit')} />}
    </div>
  );
}
